<template>
  <v-sheet class="funding-settings mt-6 pa-4" outlined>
    <div class="d-flex align-start justify-space-between flex-wrap">
      <div class="pr-4">
        <div class="font-weight-bold text-subtitle-1">목표형 프로모션</div>
        <div class="text-caption grey--text text--darken-1">
          기존 프로모션의 실제 결제액을 컨셉 공개 화면에 표시합니다.
        </div>
      </div>
      <v-switch
        class="mt-0"
        color="primary"
        hide-details
        inset
        :input-value="draft.funding_enabled"
        label="목표형 프로모션 사용"
        @change="toggleFunding(Boolean($event))"
      ></v-switch>
    </div>

    <v-expand-transition>
      <div v-if="draft.funding_enabled">
        <v-divider class="my-5"></v-divider>

        <div class="funding-group-title">기존 프로모션 연결</div>
        <v-alert v-if="promotionLoadFailed" dense text type="warning">
          프로모션 목록을 불러오지 못했습니다. 잠시 뒤 다시 열어 주세요.
        </v-alert>
        <v-select
          class="rounded-10"
          dense
          outlined
          clearable
          label="결제액을 집계할 프로모션 코드"
          hint="이 코드의 결제 잔액을 달성액으로 표시합니다. 기간과 남은 날짜도 연결한 프로모션 기준으로 자동 표시됩니다."
          persistent-hint
          :items="promotionItems"
          :loading="promotionsLoading"
          :value="draft.promotion_code"
          @input="selectPromotion($event || '')"
        ></v-select>

        <div class="funding-group-title mt-5">펀딩 기간과 방문 기간</div>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              class="rounded-10"
              dense
              outlined
              type="date"
              label="펀딩 시작일"
              :value="draft.funding_starts_on"
              @input="setValue('funding_starts_on', $event || '')"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              class="rounded-10"
              dense
              outlined
              type="date"
              label="펀딩 마감일"
              :value="draft.funding_ends_on"
              @input="setValue('funding_ends_on', $event || '')"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-alert v-if="automaticVisitPeriod" class="mt-0 mb-0" dense text type="info">
          방문일은 {{ automaticVisitPeriod }}만 선택할 수 있게 자동 설정됩니다.
        </v-alert>

        <div class="funding-group-title mt-5">공개 주소와 순서</div>
        <v-row dense>
          <v-col cols="12" md="7">
            <v-text-field
              class="rounded-10"
              dense
              outlined
              label="공개 주소"
              prefix="/funding/"
              :disabled="draft.slug_locked"
              :hint="slugHint"
              persistent-hint
              :value="draft.slug"
              @input="setValue('slug', normalizeSlug($event))"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              class="rounded-10"
              dense
              outlined
              type="number"
              min="0"
              label="목록 노출 순서"
              :value="draft.display_order"
              @input="setValue('display_order', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-select
          class="rounded-10"
          dense
          outlined
          label="공개 상태"
          :items="visibilityItems"
          :value="draft.visibility"
          @input="setValue('visibility', $event)"
        ></v-select>

        <div class="funding-group-title mt-4">목표</div>
        <v-text-field
          class="rounded-10"
          dense
          outlined
          type="number"
          min="1"
          suffix="원"
          label="목표금액"
          :value="draft.goal_amount_krw"
          @input="setValue('goal_amount_krw', $event)"
        ></v-text-field>

        <div class="funding-group-title mt-4">선착순 혜택</div>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              class="rounded-10"
              dense
              outlined
              type="number"
              min="0"
              suffix="명"
              label="1차 혜택 인원"
              hint="0명이면 선착순 혜택을 사용하지 않습니다."
              persistent-hint
              :value="draft.early_bird_limit"
              @input="setValue('early_bird_limit', $event)"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              class="rounded-10"
              dense
              outlined
              type="number"
              min="0"
              suffix="원"
              label="1차 할인 금액"
              hint="프로모션 정상가에서 이 금액만큼 할인합니다."
              persistent-hint
              :value="draft.early_bird_discount_amount"
              @input="setValue('early_bird_discount_amount', $event)"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-alert class="mt-4 mb-0" dense text type="info">
          {{ automaticContentMessage }}
        </v-alert>
        <v-alert class="mt-3 mb-0" dense text type="success">
          기존 예약·결제를 그대로 사용합니다. 별도 펀딩 결제나 주문은 만들지 않습니다.
        </v-alert>
      </div>
    </v-expand-transition>
  </v-sheet>
</template>

<script>
export default {
  props: {
    value: {
      type: Object,
      required: true,
    },
    conceptId: {
      type: [Number, String],
      default: null,
    },
    conceptTitle: {
      type: String,
      default: "",
    },
    slugSource: {
      type: String,
      default: "",
    },
    images: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    promotions: [],
    promotionsLoading: false,
    promotionLoadFailed: false,
    visibilityItems: [
      { text: "비공개", value: "private" },
      { text: "공개", value: "public" },
    ],
  }),

  computed: {
    draft() {
      return this.value
    },
    availableImages() {
      return this.images.filter(image => image && image.key && image.src)
    },
    automaticContentMessage() {
      const imageText = this.availableImages.length
        ? `위에서 등록한 컨셉 사진 ${this.availableImages.length}장을`
        : "위에서 등록할 컨셉 사진을"
      return `${imageText} 고객 화면에 자동으로 사용합니다. 제목과 설명도 프리미엄 컨셉 설명에서 자동으로 가져옵니다.`
    },
    promotionItems() {
      const conceptId = Number(this.conceptId)
      return [...this.promotions]
        .sort((a, b) => {
          const aSame = Number(a.conceptId) === conceptId ? 0 : 1
          const bSame = Number(b.conceptId) === conceptId ? 0 : 1
          return aSame - bSame
        })
        .map(promotion => ({
          text: `${promotion.name} · ${promotion.code}${
            Number(promotion.conceptId) === conceptId ? " · 현재 컨셉" : ""
          }`,
          value: promotion.code,
        }))
    },
    automaticVisitPeriod() {
      const start = this.parseDate(this.draft.funding_starts_on)
      const end = this.parseDate(this.draft.funding_ends_on)
      if (!start || !end || end < start) return ""
      const visitStart = new Date(end.getTime())
      visitStart.setUTCDate(visitStart.getUTCDate() + 1)
      const visitEnd = new Date(visitStart.getTime())
      visitEnd.setUTCMonth(visitEnd.getUTCMonth() + 1)
      visitEnd.setUTCDate(visitEnd.getUTCDate() - 1)
      return `${this.formatDate(visitStart)}부터 ${this.formatDate(visitEnd)}까지`
    },
    slugHint() {
      if (this.draft.slug_locked) {
        return "한 번 공개한 주소는 변경할 수 없습니다."
      }
      return "처음 등록할 때 자동으로 제안됩니다. 공개하기 전까지 수정할 수 있습니다."
    },
  },

  watch: {
    slugSource() {
      this.suggestSlugIfEmpty()
    },
  },

  mounted() {
    this.loadPromotions()
  },

  methods: {
    setValue(key, value) {
      this.$emit("input", { ...this.draft, [key]: value })
    },
    selectPromotion(code) {
      const promotion = this.promotions.find(item => item.code === code)
      this.$emit("input", {
        ...this.draft,
        promotion_code: code,
        funding_starts_on: promotion ? this.promotionDate(promotion.openAt) : "",
        funding_ends_on: promotion ? this.promotionDate(promotion.closeAt) : "",
      })
    },
    promotionDate(value) {
      const date = this.dayjs(value)
      return date.isValid() ? date.format("YYYY-MM-DD") : ""
    },
    parseDate(value) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return null
      const parts = value.split("-").map(Number)
      const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]))
      return date.getUTCFullYear() === parts[0] &&
        date.getUTCMonth() === parts[1] - 1 &&
        date.getUTCDate() === parts[2]
        ? date
        : null
    },
    formatDate(date) {
      return [
        String(date.getUTCFullYear()).padStart(4, "0"),
        String(date.getUTCMonth() + 1).padStart(2, "0"),
        String(date.getUTCDate()).padStart(2, "0"),
      ].join("-")
    },
    normalizeSlug(value) {
      return String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/-{2,}/g, "-")
        .replace(/^-|-$/g, "")
    },
    suggestedSlug() {
      return this.normalizeSlug(this.slugSource) ||
        (this.conceptId ? `concept-${this.conceptId}` : "")
    },
    suggestSlugIfEmpty() {
      if (!this.draft.funding_enabled || this.draft.slug || this.draft.slug_locked) return
      const slug = this.suggestedSlug()
      if (slug) this.setValue("slug", slug)
    },
    toggleFunding(enabled) {
      if (!enabled) {
        this.setValue("funding_enabled", false)
        return
      }
      this.$emit("input", {
        ...this.draft,
        funding_enabled: true,
        slug: this.draft.slug || this.suggestedSlug(),
      })
    },
    async loadPromotions() {
      this.promotionsLoading = true
      this.promotionLoadFailed = false
      try {
        const response = await this.$http.post("/api/promotion/select/getAllProductsInfo", {})
        const rows = Array.isArray(response.data) ? response.data : []
        this.promotions = rows
          .filter(item => Number(item.enabled) === 1 && item.code)
          .map(item => ({
            code: String(item.code),
            name: String(item.name || item.code),
            conceptId: Number(item.conceptId) || null,
            openAt: item.openAt || "",
            closeAt: item.closeAt || "",
          }))
        this.syncSelectedPromotionDates()
      } catch (error) {
        this.promotionLoadFailed = true
      } finally {
        this.promotionsLoading = false
      }
    },
    validate() {
      if (!this.draft.funding_enabled) return []
      const errors = []
      const goal = Number(this.draft.goal_amount_krw)
      const order = Number(this.draft.display_order || 0)
      const earlyBirdLimit = Number(this.draft.early_bird_limit || 0)
      const earlyBirdDiscount = Number(this.draft.early_bird_discount_amount || 0)

      if (!this.draft.promotion_code) {
        errors.push("결제액을 집계할 프로모션 코드를 선택해 주세요.")
      }
      const canAssignAfterInsert = !this.conceptId && !this.draft.slug
      if (!canAssignAfterInsert && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(this.draft.slug)) {
        errors.push("공개 주소를 확인해 주세요.")
      }
      if (this.draft.visibility !== "private" && this.draft.visibility !== "public") {
        errors.push("공개 상태를 확인해 주세요.")
      }
      if (!Number.isInteger(order) || order < 0) {
        errors.push("목록 노출 순서를 확인해 주세요.")
      }
      if (!Number.isInteger(goal) || goal < 1) {
        errors.push("목표금액을 확인해 주세요.")
      }
      if (!Number.isInteger(earlyBirdLimit) || earlyBirdLimit < 0) {
        errors.push("선착순 혜택 인원을 확인해 주세요.")
      }
      if (!Number.isInteger(earlyBirdDiscount) || earlyBirdDiscount < 0) {
        errors.push("선착순 할인 금액을 확인해 주세요.")
      }
      if ((earlyBirdLimit === 0) !== (earlyBirdDiscount === 0)) {
        errors.push("선착순 혜택 인원과 할인 금액을 모두 입력해 주세요.")
      }
      const fundingStart = this.parseDate(this.draft.funding_starts_on)
      const fundingEnd = this.parseDate(this.draft.funding_ends_on)
      if (!fundingStart || !fundingEnd || fundingEnd < fundingStart) {
        errors.push("펀딩 시작일과 마감일을 확인해 주세요.")
      }
      return errors
    },
    syncSelectedPromotionDates() {
      if (!this.draft.promotion_code) return
      const promotion = this.promotions.find(item => item.code === this.draft.promotion_code)
      if (!promotion) return
      this.$emit("input", {
        ...this.draft,
        funding_starts_on: this.draft.funding_starts_on || this.promotionDate(promotion.openAt),
        funding_ends_on: this.draft.funding_ends_on || this.promotionDate(promotion.closeAt),
      })
    },
  },
}
</script>

<style scoped>
.funding-settings {
  border-color: #d8c3ca !important;
  border-radius: 10px;
  background: #fffafb;
}

.funding-group-title {
  margin-bottom: 12px;
  color: #6c3d4b;
  font-size: 14px;
  font-weight: 700;
}
</style>
